export function flatten(arr) {
  return Array.isArray(arr) ? arr.reduce((acc, v) => acc.concat(flatten(v)), []) : [arr];
}

// Recursive parser for nested WhatsApp formatting
export function parseRichText(str) {
  if (!str) return '';

  // Patterns (no global flag) - we will pick the earliest/outermost match across all patterns
  const patterns = [
    { regex: /```([^`\n]+?)```/, render: (c, k) => <code key={k} style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '0.9em' }}>{c}</code> },
    { regex: /__([^_\n]+?)__/, render: (c, k) => <u key={k}>{c}</u> },
    { regex: /~([^~\n]+?)~/, render: (c, k) => <del key={k}>{c}</del> },
    { regex: /\*([^*\n]+?)\*/, render: (c, k) => <strong key={k}>{c}</strong> },
    { regex: /_(?!_)([^_\n]+?)_(?!_)/, render: (c, k) => <em key={k}>{c}</em> },
  ];

  // Links
  const linkRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  // Find the earliest (outermost) match among all patterns
  let best = null;
  for (const p of patterns) {
    // create a fresh regex instance to avoid state issues
    const regex = new RegExp(p.regex.source, p.regex.flags);
    const m = regex.exec(str);
    if (m) {
      if (!best) {
        best = { pattern: p, match: m };
      } else {
        // prefer earlier start; if equal, prefer longer match
        if (m.index < best.match.index || (m.index === best.match.index && m[0].length > best.match[0].length)) {
          best = { pattern: p, match: m };
        }
      }
    }
  }

  if (best) {
    const { pattern, match } = best;
    const before = str.slice(0, match.index);
    const inner = match[1]; // captured group
    const after = str.slice(match.index + match[0].length);
    return flatten([
      parseRichText(before),
      pattern.render(parseRichText(inner), match.index),
      parseRichText(after)
    ]);
  }

  // No formatting matches - handle links and line breaks
  // First handle links
  const linkParts = [];
  let lastIndex = 0;
  let lm;
  while ((lm = linkRegex.exec(str)) !== null) {
    if (lm.index > lastIndex) {
      linkParts.push(str.slice(lastIndex, lm.index));
    }
    const url = lm[0].startsWith('www.') ? `https://${lm[0]}` : lm[0];
    linkParts.push(
      <a
        key={`link-${lm.index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
      >
        {lm[0]}
      </a>
    );
    lastIndex = lm.index + lm[0].length;
  }
  if (lastIndex < str.length) {
    linkParts.push(str.slice(lastIndex));
  }
  if (linkParts.length > 0) {
    // handle line breaks inside linkParts
    return flatten(linkParts).flatMap((part, idx, arr) => {
      if (typeof part !== 'string') return part;
      return part.split('\n').flatMap((line, li, la) => {
        const nodes = [line];
        if (li < la.length - 1) nodes.push(<br key={`br-link-${idx}-${li}`} />);
        return nodes;
      });
    });
  }

  // Finally handle plain text line breaks
  return str.split('\n').reduce((acc, line, idx, arr) => {
    acc.push(line);
    if (idx < arr.length - 1) acc.push(<br key={`br-${idx}`} />);
    return acc;
  }, []);
}

export function stripRichFormatting(input) {
  if (input == null) return '';
  let s = String(input);

  // Patterns mirror parseRichText order; allow multiline for monospace
  const patterns = [
    { regex: /```([\s\S]*?)```/g },           // monospace block
    { regex: /__([^_\n]+?)__/g },             // underline
    { regex: /~([^~\n]+?)~/g },               // strikethrough
    { regex: /\*([^*\n]+?)\*/g },             // bold
    { regex: /_(?!_)([^_\n]+?)_(?!_)/g },     // italic (single underscore)
  ];

  // Iteratively remove markers until stable (handles nesting)
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of patterns) {
      const replaced = s.replace(p.regex, (_, inner) => {
        // keep inner content
        return typeof inner === 'undefined' ? '' : inner;
      });
      if (replaced !== s) {
        changed = true;
        s = replaced;
      }
    }
  }

  return s;
}